// Public branding fetched from /api/v1/public/tenants/{slug}/branding.
// Schema mirrors apps/api PublicBranding.

import { z } from "zod";

export const PublicBrandingSchema = z.object({
  tenant_id: z.string(),
  name: z.string(),
  primary_color: z.string().optional(),
  accent: z.string().optional(),
  font: z.string().optional(),
  logo_url: z.string().optional(),
  demo_css_vars: z.record(z.string(), z.string()).optional(),
  ai_disclosure: z.string().optional(),
  widget_locale: z.string().optional(),
  show_powered_by: z.boolean().optional(),
  // Mirrors the API's support_handoff_enabled — the widget must not offer
  // "Talk to a human" to a tenant that has support turned off. Defaults
  // to false so an old/unaware API response (field absent) behaves the
  // same as an explicit false, rather than leaving the widget to guess.
  support_handoff_enabled: z.boolean().default(false),
});
export type PublicBranding = z.infer<typeof PublicBrandingSchema>;

const ALLOWED_VARS = new Set([
  "primary",
  "primary_fg",
  "accent",
  "accent_fg",
  "bg",
  "fg",
  "muted",
  "border",
  "font",
]);

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Convert tenant CSS variables to a sanitised object ready to apply on
 * the widget's Shadow DOM host. The widget renders inside a Shadow DOM
 * so a misbehaving value can't escape the boundary — but we still
 * gate input keys + hex regex as defence in depth. Tenants ship
 * untrusted-by-default content (custom prompts, model outputs); we
 * don't want any of that to reach the host page's CSS through the
 * widget.
 */
export function sanitiseBrandVars(
  vars: Record<string, string> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!vars) return out;
  for (const [k, v] of Object.entries(vars)) {
    if (!ALLOWED_VARS.has(k)) continue;
    if (k === "font") {
      const f = v.trim().slice(0, 200);
      if (f) out["--brand-font"] = f;
      continue;
    }
    if (HEX.test(v)) {
      out[`--brand-${k}`] = v.toLowerCase();
    }
  }
  return out;
}

/**
 * Fetch and validate the public branding doc for a tenant. Returns null
 * for 404 (tenant not found / suspended) so the widget can fall back to
 * neutral defaults instead of breaking the host page.
 */
export async function fetchPublicBranding(
  apiBase: string,
  slug: string,
  signal?: AbortSignal,
): Promise<PublicBranding | null> {
  const url = `${apiBase.replace(/\/$/, "")}/api/v1/public/tenants/${encodeURIComponent(slug)}/branding`;
  const res = await fetch(url, { signal });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`branding fetch ${slug} → ${res.status}`);
  }
  return PublicBrandingSchema.parse(await res.json());
}

// --- /demo endpoint: branding + agent metadata + suggestion chips ---

export const PublicAgentSchema = z.object({
  id: z.string(),
  name: z.string(),
  purpose: z.string().optional().default(""),
  suggestions: z.array(z.string()).default([]),
});
export type PublicAgent = z.infer<typeof PublicAgentSchema>;

export const PublicDemoSchema = z.object({
  tenant_id: z.string(),
  name: z.string(),
  branding: PublicBrandingSchema,
  agent: PublicAgentSchema.optional(),
});
export type PublicDemo = z.infer<typeof PublicDemoSchema>;

export async function fetchPublicDemo(
  apiBase: string,
  slug: string,
  signal?: AbortSignal,
): Promise<PublicDemo | null> {
  const url = `${apiBase.replace(/\/$/, "")}/api/v1/public/tenants/${encodeURIComponent(slug)}/demo`;
  const res = await fetch(url, { signal });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`demo fetch ${slug} → ${res.status}`);
  }
  return PublicDemoSchema.parse(await res.json());
}

// --- /widgets endpoint: list of configured widgets (welcome copy etc.) ---

export const PublicWidgetSchema = z.object({
  id: z.string(),
  name: z.string().default(""),
  welcome: z.string().optional().default(""),
  welcome_buttons: z.array(z.object({ label: z.string(), message: z.string() })).optional(),
});
export type PublicWidget = z.infer<typeof PublicWidgetSchema>;

export const PublicWidgetsResponseSchema = z.object({
  default_widget_id: z.string().default(""),
  widgets: z.array(PublicWidgetSchema).default([]),
});
export type PublicWidgetsResponse = z.infer<typeof PublicWidgetsResponseSchema>;

/**
 * Fetch the tenant's widget list. Returns null on 404 or network error so
 * the widget degrades gracefully to a generic greeting if widgets haven't
 * been configured yet.
 */
export async function fetchPublicWidgets(
  apiBase: string,
  slug: string,
  signal?: AbortSignal,
): Promise<PublicWidgetsResponse | null> {
  const url = `${apiBase.replace(/\/$/, "")}/api/v1/public/tenants/${encodeURIComponent(slug)}/widgets`;
  const res = await fetch(url, { signal });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`widgets fetch ${slug} → ${res.status}`);
  }
  return PublicWidgetsResponseSchema.parse(await res.json());
}

// --- /chat endpoint: send one visitor message, get one bot reply ---

// Rich content (richcard / carousel / suggestion chips). The widget
// renders these inside the Shadow DOM. Optional — when the backend
// returns text only, the widget falls back to a plain bubble.
export const AgentSuggestionSchema = z.object({
  kind: z.string().default("reply"),
  text: z.string(),
  postback: z.string().default(""),
  action: z.string().optional().default(""),
  to: z.string().optional().default(""),
});
export type AgentSuggestion = z.infer<typeof AgentSuggestionSchema>;

export const AgentRichCardSchema = z.object({
  product_id: z.string().optional().default(""),
  title: z.string(),
  description: z.string(),
  media: z
    .object({
      url: z.string(),
      type: z.string().optional().default(""),
      thumbnail: z.string().optional().default(""),
    })
    .optional(),
  suggestions: z.array(AgentSuggestionSchema).default([]),
});
export type AgentRichCard = z.infer<typeof AgentRichCardSchema>;

export const AgentResponseSchema = z.object({
  kind: z.string(),
  text: z.string().default(""),
  richcard: z.array(AgentRichCardSchema).default([]),
  suggestions: z.array(AgentSuggestionSchema).default([]),
  carousel_card_width: z.string().optional(),
  carousel_card_layout: z.object({ cardOrientation: z.string().optional() }).optional(),
});
export type AgentResponse = z.infer<typeof AgentResponseSchema>;

// HandoffPayloadSchema mirrors handler.handoffPayload (apps/api/internal/
// handler/public_handoff.go): the ticket the "Talk to a human" affordance
// (or the LLM's own handover_to_human tool call) just opened or reused,
// the holding-message text, and the wait/continue choices as localized
// labels — server-rendered, wait always first. Choices is empty once a
// human already owns the ticket (composeHandoffPayload), since offering
// them then would just 409 on /mode — the widget must not invent its own
// fallback labels for that case, an empty array means "no choice to make".
export const HandoffPayloadSchema = z.object({
  ticket_id: z.string(),
  text: z.string(),
  choices: z.array(z.string()).optional().default([]),
});
export type HandoffPayload = z.infer<typeof HandoffPayloadSchema>;

export const PublicChatResponseSchema = z.object({
  conversation_id: z.string().default(""),
  text: z.string(),
  response: AgentResponseSchema.optional(),
  // HumanState mirrors conv.Conversation.HumanState — "" | "pending" |
  // "waiting" | "active" — so the widget knows a person is involved and
  // opens (or keeps open) the live stream. Empty on the ordinary bot
  // path. See stream.ts / chatmerce-chat.ts ensureStream().
  human_state: z.string().optional().default(""),
  // ClientToken is the plaintext per-conversation bearer secret (spec
  // P2), returned ONLY on the turn that mints it — the very first turn
  // of a brand-new thread. The widget persists it in localStorage
  // beside the conversation id on that first response; the server
  // never echoes it again.
  client_token: z.string().optional().default(""),
  // Present when this turn opened (or reused) a ticket — either the
  // visitor typed a whole-message trigger phrase the server classified,
  // or the LLM itself called handover_to_human. The widget renders this
  // IDENTICALLY to a direct POST …/handover response (chatmerce-chat.ts
  // renderHandoffMessage) — the visitor must not be able to tell which
  // path opened the ticket.
  handoff: HandoffPayloadSchema.optional(),
  // ContactPrompt mirrors handler.chatResponse.ContactPrompt — the
  // localized "leave your contact details" nudge (handoff.ContactPrompt),
  // present on the one response that just moved the conversation into
  // HumanStateWaiting from the pending intercept (Task 10, design spec
  // §4: a visitor's TYPED answer, classified server-side, not a chip
  // tap — the wait/continue chip's own POST …/mode never carries this).
  // Empty on every other response, including the /mode response for the
  // exact same transition. The widget uses this verbatim as the contact
  // form's own prompt text when present, and falls back to a localized
  // default otherwise (chatmerce-chat.ts renderContactForm()).
  contact_prompt: z.string().optional().default(""),
});
export type PublicChatResponse = z.infer<typeof PublicChatResponseSchema>;

// Header name for the per-conversation client token (spec P2). Matches
// the server's handler.ClientTokenHeader (apps/api/internal/handler/
// client_token.go) and the identical constant stream.ts defines for the
// REST catch-up — kept as a literal in both rather than a shared import
// since it's one string, not a coupling worth introducing.
const CLIENT_TOKEN_HEADER = "X-Chatmerce-Client-Token";

// The exact 403 body the server sends when a request carries a
// conversation_id whose stored client token doesn't match the presented
// one (or none was presented at all) — public_demo.go's chat handler,
// same string the stream endpoints use. sendPublicChat turns this into
// a distinguishable Error so the caller can drop the dead conversation
// and retry once as a fresh thread, without ever doing that for any
// OTHER 403 (rate limiting, origin, a real server error).
const INVALID_CLIENT_TOKEN_BODY = "invalid client token";
export const ERR_INVALID_CLIENT_TOKEN = "invalid_client_token";

// The exact 409 body postMode sends when the ticket is already
// ticket.StatusActive (public_handoff.go) — a human already claimed the
// thread, whether by staff "Take over" or a concurrent tab's own mode
// choice winning a race. sendPublicMode turns this into a distinguishable
// Error so the caller renders the "you're now with a person" banner
// instead of an error toast: this is good news for the visitor, not a
// failure.
const HANDOFF_ACTIVE_BODY = "ticket is active";
export const ERR_HANDOFF_ACTIVE = "handoff_active";

// The exact 409 body postMode sends for its OWN ErrInvalidTransition
// branch (public_handoff.go) — the ticket was claimed, resolved, or
// expired between its own Get and Update, racing past the handler's
// earlier StatusActive check. "ticket is active" would be a lie for the
// resolved/expired case, so the server says something true of all of
// them instead.
const MODE_CLOSED_BODY = "ticket is closed";

// Fix Round 2 (mirror of the /contact fix), Fix Round 3 (exact-match
// the closed body too, don't just assume): postMode's OTHER two failure
// shapes both mean the same thing — nothing is left to change the mode
// OF, and both are genuinely terminal (unlike a 429/5xx), so both get
// the SAME sentinel. A 404 means c.ActiveTicketID was already empty (or
// the ticket itself vanished) by the time this request landed — in
// particular, once Resolve()'s mirror-clear has run for a ticket that
// resolved or expired concurrently; a 404 is unambiguous, so every one
// maps here. A 409 maps here ONLY on an exact match against
// MODE_CLOSED_BODY — round 2 mapped ANY 409 that wasn't
// HANDOFF_ACTIVE_BODY here, which was correct against postMode's
// CURRENT two 409 shapes but coupled that safety to a comment, not a
// check: if the server string ever drifts (a typo fix, capitalization,
// i18n), an unrecognised 409 would silently assert the conversation is
// closed — suppressing the "with a person" banner and resurfacing "Talk
// to a human" on a genuinely active takeover. An unmatched 409 now falls
// through to the generic error path instead (see sendPublicMode below),
// which merely re-arms the wait-timeout offer — recoverable, unlike a
// false "closed".
export const ERR_MODE_CLOSED = "mode_closed";

export async function sendPublicChat(
  apiBase: string,
  slug: string,
  sessionID: string,
  message: string,
  conversationID = "",
  widgetKey = "",
  signal?: AbortSignal,
  locale = "",
  // clientToken is the per-conversation bearer secret (spec P2). Every
  // turn on a thread whose conversation was minted WITH a token must
  // present it — Task 13's /chat refuses a conversation_id otherwise,
  // including when the header is simply absent (not just wrong). Added
  // last, defaulted, so no existing positional call site shifts.
  clientToken = "",
): Promise<PublicChatResponse> {
  const url = `${apiBase.replace(/\/$/, "")}/api/v1/public/tenants/${encodeURIComponent(slug)}/chat`;
  const body: Record<string, string> = { session_id: sessionID, message };
  if (conversationID) body.conversation_id = conversationID;
  // locale is the widget's resolved display locale (lang attr > tenant
  // widget_locale > sniffed). Sent so the AGENT answers in the visitor's
  // language instead of inferring it per message — an embed with
  // lang="en" used to leave the model guessing, and it guessed Polish on
  // short or ambiguous input. See agentrun.replyLanguageDirective.
  if (locale) body.locale = locale;
  // widget_key is the server-generated random token from the embed
  // snippet's widget="..." attribute. Forwarding it lets the chat
  // handler enforce per-widget origin allowlists; omitting it falls
  // back to the tenant-level allowlist (legacy demo path).
  if (widgetKey) body.widget_key = widgetKey;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  // Only a conversation the server minted WITH a token needs it, and
  // the widget only ever holds one once resp.client_token has arrived
  // — so this is empty (and omitted) on the very turn that creates the
  // conversation, and on every legacy conversation from before tokens
  // existed.
  if (clientToken) headers[CLIENT_TOKEN_HEADER] = clientToken;
  const res = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    if (res.status === 429) throw new Error("rate_limited");
    if (res.status === 403) {
      // Read defensively: a body-read failure must not masquerade as
      // the specific "drop and retry" signal below.
      const text = await res.text().catch(() => "");
      if (text.trim() === INVALID_CLIENT_TOKEN_BODY) {
        throw new Error(ERR_INVALID_CLIENT_TOKEN);
      }
      throw new Error(`chat send → 403: ${text.trim() || "forbidden"}`);
    }
    throw new Error(`chat send → ${res.status}`);
  }
  return PublicChatResponseSchema.parse(await res.json());
}

// --- Task 8 endpoints: the "Talk to a human" affordance and the
// wait/continue choice. Token-authenticated (same client-token header as
// /chat), but deliberately NOT routed through /chat: a button press or a
// chip tap must not spend an LLM turn or the tenant's monthly quota —
// that is the entire reason these two routes exist (design spec §8).

function handoffURL(apiBase: string, slug: string, convID: string, route: string): string {
  return `${apiBase.replace(/\/$/, "")}/api/v1/public/tenants/${encodeURIComponent(slug)}/conversations/${encodeURIComponent(convID)}/${route}`;
}

/**
 * POST …/handover — opens (or reuses) a ticket for convID and returns the
 * holding-message payload the widget renders. This is the "Talk to a
 * human" affordance's target.
 */
export async function sendPublicHandover(
  apiBase: string,
  slug: string,
  convID: string,
  clientToken: string,
  locale = "",
  signal?: AbortSignal,
): Promise<HandoffPayload> {
  const body: Record<string, string> = {};
  if (locale) body.locale = locale;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (clientToken) headers[CLIENT_TOKEN_HEADER] = clientToken;
  const res = await fetch(handoffURL(apiBase, slug, convID, "handover"), {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    if (res.status === 429) throw new Error("rate_limited");
    if (res.status === 403) {
      const text = await res.text().catch(() => "");
      if (text.trim() === INVALID_CLIENT_TOKEN_BODY) {
        throw new Error(ERR_INVALID_CLIENT_TOKEN);
      }
      throw new Error(`handover → 403: ${text.trim() || "forbidden"}`);
    }
    throw new Error(`handover → ${res.status}`);
  }
  return HandoffPayloadSchema.parse(await res.json());
}

// The two customer-facing modes this route accepts — mirrors handler.
// requestModes (public_handoff.go), which deliberately excludes "pending":
// that is the system's own pre-answer state, not something a customer
// action can request back into.
export type HandoffMode = "waiting" | "bot_ok";

export const ModeResponseSchema = z.object({
  mode: z.string(),
  human_state: z.string().optional().default(""),
});
export type ModeResponse = z.infer<typeof ModeResponseSchema>;

/**
 * POST …/mode — records the visitor's wait-or-continue answer (or the
 * wait-timeout's "go back to the assistant" escape hatch). A 409 whose
 * body is exactly "ticket is active" means a human already claimed the
 * ticket (handler.postMode's `tk.Status == StatusActive` check) — good
 * news for the visitor, not a failure, so it is surfaced via the
 * distinguishable ERR_HANDOFF_ACTIVE rather than a bare status check,
 * the same way ERR_INVALID_CLIENT_TOKEN distinguishes one specific 403
 * body from every other one. A 404, or any OTHER 409, means the
 * opposite news — the ticket resolved/expired/vanished — and is
 * surfaced as ERR_MODE_CLOSED instead (see its own doc comment).
 */
export async function sendPublicMode(
  apiBase: string,
  slug: string,
  convID: string,
  clientToken: string,
  mode: HandoffMode,
  signal?: AbortSignal,
): Promise<ModeResponse> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (clientToken) headers[CLIENT_TOKEN_HEADER] = clientToken;
  const res = await fetch(handoffURL(apiBase, slug, convID, "mode"), {
    method: "POST",
    headers,
    body: JSON.stringify({ mode }),
    signal,
  });
  if (!res.ok) {
    if (res.status === 429) throw new Error("rate_limited");
    // Fix Round 2: c.ActiveTicketID already empty, or the ticket itself
    // gone — see ERR_MODE_CLOSED's own doc comment.
    if (res.status === 404) throw new Error(ERR_MODE_CLOSED);
    if (res.status === 409) {
      const text = await res.text().catch(() => "");
      const body = text.trim();
      if (body === HANDOFF_ACTIVE_BODY) {
        throw new Error(ERR_HANDOFF_ACTIVE);
      }
      if (body === MODE_CLOSED_BODY) {
        throw new Error(ERR_MODE_CLOSED);
      }
      // Fix Round 3: an UNRECOGNISED 409 body falls through to the
      // generic path rather than assuming ERR_MODE_CLOSED — see
      // ERR_MODE_CLOSED's own doc comment for why asserting "closed" on
      // a drifted server string would be the worse failure mode.
      throw new Error(`mode → 409: ${body || "conflict"}`);
    }
    if (res.status === 403) {
      const text = await res.text().catch(() => "");
      if (text.trim() === INVALID_CLIENT_TOKEN_BODY) {
        throw new Error(ERR_INVALID_CLIENT_TOKEN);
      }
      throw new Error(`mode → 403: ${text.trim() || "forbidden"}`);
    }
    throw new Error(`mode → ${res.status}`);
  }
  return ModeResponseSchema.parse(await res.json());
}

// maxContactEmailLen mirrors the server's own hard cap (RFC 5321,
// handler.maxContactEmailLen) — kept here only so an obviously-oversized
// value never leaves the browser; the server remains the source of
// truth for the real limit. Exported so chatmerce-chat.ts's own
// client-side check uses the SAME number rather than a second literal
// that could drift from this one (Fix Round 1, Important 2).
export const MAX_CONTACT_EMAIL_LEN = 254;

// The 400 body postContact sends for an address it rejects (empty, too
// long, or mail.ParseAddress fails) — public_handoff.go. Distinguished
// so the widget can show the SAME "enter a valid email" copy the
// client-side check shows, rather than a generic "try again" that would
// invite a retry of an address that can never be accepted (Fix Round 1,
// Important 2).
export const ERR_CONTACT_INVALID_EMAIL = "contact_invalid_email";

// The 404 (no active ticket) / 409 (ticket not open/active — e.g.
// already resolved) bodies postContact sends — public_handoff.go. Both
// mean the same thing to the visitor: there is nothing left to submit
// this address TO, so a retry is structurally impossible (Fix Round 1,
// Important 2) — unlike a 429/5xx, which are transient.
export const ERR_CONTACT_CLOSED = "contact_closed";

/**
 * POST …/contact — records (or corrects) the visitor's fallback contact
 * email on the ticket opened by the wait choice (Task 16, design spec
 * §13). 204 on success, thrown Error otherwise. This is the ONLY
 * network call the contact form ever makes: a client-rejected (empty,
 * too long, or pattern-invalid) address never reaches this function at
 * all — chatmerce-chat.ts validates before calling it, so "posts
 * nothing" for an invalid email is a property of the caller, not this
 * sender.
 */
export async function sendPublicContact(
  apiBase: string,
  slug: string,
  convID: string,
  clientToken: string,
  email: string,
  signal?: AbortSignal,
): Promise<void> {
  if (!email || email.length > MAX_CONTACT_EMAIL_LEN) {
    throw new Error(ERR_CONTACT_INVALID_EMAIL);
  }
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (clientToken) headers[CLIENT_TOKEN_HEADER] = clientToken;
  const res = await fetch(handoffURL(apiBase, slug, convID, "contact"), {
    method: "POST",
    headers,
    body: JSON.stringify({ email }),
    signal,
  });
  if (!res.ok) {
    if (res.status === 429) throw new Error("rate_limited");
    if (res.status === 400) throw new Error(ERR_CONTACT_INVALID_EMAIL);
    if (res.status === 404 || res.status === 409) throw new Error(ERR_CONTACT_CLOSED);
    if (res.status === 403) {
      const text = await res.text().catch(() => "");
      if (text.trim() === INVALID_CLIENT_TOKEN_BODY) {
        throw new Error(ERR_INVALID_CLIENT_TOKEN);
      }
      throw new Error(`contact → 403: ${text.trim() || "forbidden"}`);
    }
    throw new Error(`contact → ${res.status}`);
  }
}
